import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getSurvey, createSurvey } from "../../lib/Survey/action";
import { SUBSCRIPTION_PLANS } from "../../public/Constants/CountriesList";

const defaultSurveyInfo = {
    surveyType: "regular",
    influencerName: null,
    simaVersion: null,
};

const initialQuestionState = {
    current: "screen1",
    direction: -1,
    stack: [],
};

const initialState = {
    userSurvey: null,
    surveyAnswers: {},
    surveyQuestions: initialQuestionState,
    surveyLoading: false,
    surveyInfo: defaultSurveyInfo,
    fieldsValidated: false,
};

const useSurveyStore = create(
    persist(
        (set, get) => ({
            ...initialState,
            hasSurveyHydrated: false,

            resetSurvey: () => set(initialState),

            setSurveyAnswers: (answers) => set({ surveyAnswers: answers }),

            setSurveyQuestion: ({ next, dir }) => {
                const {
                    surveyQuestions: { current, stack },
                } = get();

                const updatedStack = [...stack];
                const newState =
                    dir === -1
                        ? {
                              current: next,
                              direction: dir,
                              stack: [...stack, current],
                          }
                        : dir === 1 && stack.length
                          ? {
                                current: updatedStack.pop(),
                                direction: dir,
                                stack: updatedStack,
                            }
                          : null;

                if (newState) set({ surveyQuestions: newState });
            },

            getUserSurvey: async () => {
                set({ surveyLoading: true });
                try {
                    const userSurvey = await getSurvey();
                    if (userSurvey) {
                        set({ userSurvey });
                    } else {
                        console.warn("No survey data found.");
                    }
                } catch (error) {
                    console.error("Failed to fetch survey:", error);
                } finally {
                    set({ surveyLoading: false });
                }
            },

            createUserSurvey: async (payload) => {
                set({ surveyLoading: true });

                try {
                    const userSurvey = await createSurvey(payload);

                    if (userSurvey) {
                        set({ userSurvey });
                        return userSurvey;
                    } else {
                        console.warn(
                            "Survey creation returned no data. Check backend response or payload:",
                            payload
                        );
                    }
                } catch (error) {
                    console.error(
                        "Unexpected error during survey creation:",
                        error.message || error
                    );
                } finally {
                    set({ surveyLoading: false });
                }
            },

            setSurveyType: (surveyType) => {
                set({
                    surveyInfo: {
                        surveyType,
                        influencerName: null,
                        simaVersion: null,
                    },
                });
            },

            setSurveyInfo: ({
                sevenStepSurvey,
                influencerSurvey,
                influencerName,
                subscription,
                isSimaEng,
                isSima,
            }) => {
                let surveyType = "regular";

                if (SUBSCRIPTION_PLANS?.includes(subscription)) {
                    surveyType = subscription;
                } else if (sevenStepSurvey) {
                    surveyType = "seven_step";
                } else if (influencerSurvey && influencerName) {
                    surveyType = "influencer";
                }

                let simaVersion = null;

                if (sevenStepSurvey) {
                    if (isSima) {
                        simaVersion = "ar";
                    } else if (isSimaEng) {
                        simaVersion = "eng";
                    }
                }

                set({
                    surveyInfo: {
                        surveyType,
                        influencerName:
                            surveyType === "influencer" ? influencerName : null,
                        simaVersion,
                    },
                });
            },

            setHasHydrated: (state) => {
                set({
                    hasSurveyHydrated: state,
                });
            },

            setFieldsValidated: (state) => {
                set({
                    fieldsValidated: state,
                });
            },
        }),
        {
            name: "survey-store", // key for local storage
            getStorage: () => localStorage,
            partialize: (state) => ({
                surveyInfo: state.surveyInfo,
                surveyAnswers: state.surveyAnswers,
                surveyQuestions: state.surveyQuestions,
            }),
            onRehydrateStorage: (state) => {
                return () => state.setHasHydrated(true);
            },
        }
    )
);

export default useSurveyStore;
